export interface CommutifiErrorData {
  code?: number | string;
  httpCode: number;
  isCommutifiError: boolean;
  level: string;
  message: string;
  name: string;
  stack: string;
}

/**
 * Generic HTTP response shape - not tied to any specific HTTP client (Axios, fetch, etc.)
 */
export type HttpResponseLike<T = unknown> = {
  status: number;
  statusText?: string;
  data?: T;
  config?: {
    url?: string;
    method?: string;
  };
};

/**
 * Custom error class for API responses that provides meaningful error messages
 * instead of "[object Object]" when logged or captured by Sentry.
 *
 * Preserves full response data (status, headers, data) while being a proper Error instance.
 * Works with any HTTP client (Axios, fetch, etc.) - just pass a response-like object.
 */
export class ApiError<T = unknown> extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly data: T | undefined;
  readonly url: string;
  readonly method: string;
  constructor(response: HttpResponseLike<T>) {
    const method = response.config?.method?.toUpperCase() || 'REQUEST';
    const url = response.config?.url || 'unknown';
    const errorMessage = response.data && typeof response.data === 'object' && 'message' in response.data ? String((response.data as {
      message?: unknown;
    }).message) : undefined;
    const message = errorMessage ? `${method} ${url} failed: ${response.status} - ${errorMessage}` : `${method} ${url} failed with status ${response.status}`;
    super(message);
    this.name = 'ApiError';
    this.status = response.status;
    this.statusText = response.statusText || '';
    this.data = response.data;
    this.url = url;
    this.method = method;

    // Maintains proper stack trace for where error was thrown (only in V8 engines)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  /**
   * Type guard to check if an error is an ApiError
   */
  static isApiError<T = unknown>(error: unknown): error is ApiError<T> {
    return error instanceof ApiError;
  }
}

/**
 * Custom error class for network errors (no response received).
 * This occurs when the request was made but no response was received
 * (e.g., network timeout, DNS failure, CORS blocked).
 *
 * Works with any HTTP client - not tied to XMLHttpRequest or any specific implementation.
 */
export class NetworkError extends Error {
  readonly url: string | undefined;
  constructor(url?: string) {
    const message = url ? `Network error for ${url}` : 'Network error - no response received';
    super(message);
    this.name = 'NetworkError';
    this.url = url;
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NetworkError);
    }
  }

  /**
   * Type guard to check if an error is a NetworkError
   */
  static isNetworkError(error: unknown): error is NetworkError {
    return error instanceof NetworkError;
  }
}